import { DataEHoraAmigavel } from '@/shared/utils/datas';
import { GetNotificacoes, SetNotificacao } from '@/modules/system/notificacoes/notificacoes.service';
import {
  notificacaoDetalhesExibicao,
  notificacaoTituloExibicao,
} from '@/modules/system/notificacoes/notificacao-display.utils';
import { Link, useNavigate } from 'react-router-dom';
import { Links } from '@/shared/constants/navegacao';
import { NotificacaoDto } from '@/api.types';
import { RiNotificationLine } from 'react-icons/ri';
import { useEffect, useState, useRef } from 'react';
import { useRelatoriosNotificacoesBell } from '@/modules/reports/presentation/hooks/useRelatoriosNotificacoesBell';
import {
  getTipoLabelRelatorio,
  type RelatorioNotificacaoItem,
} from '@/modules/reports/application/relatorios-notificacoes.utils';

type NotificationBellVariant = 'navbar' | 'header';

interface NotificationBellProps {
  variant?: NotificationBellVariant;
}

function iconeRelatorio(tipo: RelatorioNotificacaoItem['tipo']) {
  switch (tipo) {
    case 'envio':
      return <i className="bi bi-check-circle-fill text-success" aria-hidden />;
    case 'feedback':
      return <i className="bi bi-chat-dots-fill text-info" aria-hidden />;
    case 'prazo':
      return <i className="bi bi-calendar-event-fill text-warning" aria-hidden />;
    case 'validacao_pendente':
      return <i className="bi bi-clipboard-check-fill text-primary" aria-hidden />;
    case 'correcao':
      return <i className="bi bi-exclamation-triangle-fill text-warning" aria-hidden />;
    default:
      return <i className="bi bi-bell-fill text-secondary" aria-hidden />;
  }
}

export default function NotificationBell({ variant = 'navbar' }: NotificationBellProps) {
  const navigate = useNavigate();
  const [notificacoes, setNotificacoes] = useState<NotificacaoDto[]>([]);
  const [notificacoesCount, setNotificacoesCount] = useState(0);
  const [showNotificacoes, setShowNotificacoes] = useState(false);
  const [relatoriosRefreshToken, setRelatoriosRefreshToken] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    loading: loadingRelatorios,
    notificacoes: notificacoesRelatorios,
    count: relatoriosCount,
    ocultarNotificacao,
  } = useRelatoriosNotificacoesBell({ refreshToken: relatoriosRefreshToken });

  const badgeTotal = notificacoesCount + relatoriosCount;

  const carregarNotificacoes = async () => {
    try {
      const resultado = await GetNotificacoes(true);
      if (resultado && resultado.objects) {
        setNotificacoes(resultado.objects.slice(0, 5));
        setNotificacoesCount(resultado.count || 0);
      }
    } catch {
      // silencioso — mesmo comportamento anterior
    }
  };

  useEffect(() => {
    carregarNotificacoes();

    intervalRef.current = setInterval(() => {
      carregarNotificacoes();
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        showNotificacoes &&
        !target.closest('#dropdownNotificacoes') &&
        !target.closest('.dropdown-menu')
      ) {
        setShowNotificacoes(false);
      }
    };

    if (showNotificacoes) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotificacoes]);

  const handleMarcarComoLida = async (id: string) => {
    try {
      await SetNotificacao(id);
      carregarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const getLinkParaNotificacao = (notificacao: NotificacaoDto): string | null => {
    if (!notificacao.idregistro) {
      return null;
    }

    switch (notificacao.tipo) {
      case 'ACAO':
        return Links.Acoes_Editar + '/' + notificacao.idregistro;
      case 'LOTACAO':
        return Links.Vinculos_Editar + '/' + notificacao.idregistro;
      case 'PESSOA':
        return Links.Pessoa_Editar + '/' + notificacao.idregistro;
      default:
        return Links.Acoes_Editar + '/' + notificacao.idregistro;
    }
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    const next = !showNotificacoes;
    setShowNotificacoes(next);
    if (next) {
      setRelatoriosRefreshToken((t) => t + 1);
      void carregarNotificacoes();
    }
  };

  const isHeader = variant === 'header';

  return (
    <div className={isHeader ? 'app-header__notifications' : undefined}>
      <Link
        className={
          isHeader
            ? 'app-header__notif-btn position-relative'
            : 'nav-link position-relative'
        }
        to="#"
        id="dropdownNotificacoes"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded={showNotificacoes}
        aria-label="Notificações"
        title="Notificações"
        onClick={toggleDropdown}
      >
        <RiNotificationLine size={isHeader ? 18 : 14} color={isHeader ? undefined : '#fff'} />
        {badgeTotal > 0 && (
          <span
            className={
              isHeader
                ? 'position-absolute translate-middle badge rounded-pill app-header__notif-badge'
                : 'position-absolute top-5 start-100 translate-middle badge rounded-pill'
            }
            style={isHeader ? undefined : { fontSize: '0.7rem', minWidth: '18px' }}
          >
            {badgeTotal > 99 ? '99+' : badgeTotal}
          </span>
        )}
      </Link>
      <div
        className={`dropdown-menu dropdown-menu-end ${showNotificacoes ? 'show' : ''}`}
        aria-labelledby="dropdownNotificacoes"
        style={{
          minWidth: '360px',
          maxWidth: '420px',
          maxHeight: '520px',
          overflowY: 'auto',
        }}
      >
        {/* Seção 1 — Sistema */}
        <div className="dropdown-header d-flex justify-content-between align-items-center py-2">
          <strong className="small text-uppercase text-muted" style={{ letterSpacing: '0.04em' }}>
            Sistema
          </strong>
          <Link
            to={Links.Notificacoes_index}
            className="text-decoration-none small"
            onClick={() => setShowNotificacoes(false)}
          >
            Ver todas
          </Link>
        </div>
        <div className="dropdown-divider my-0" />
        {notificacoes.length === 0 ? (
          <div className="dropdown-item-text text-muted text-center py-3 small">
            Nenhuma notificação do sistema
          </div>
        ) : (
          notificacoes.map((notificacao, index) => {
            const titulo = notificacaoTituloExibicao(notificacao.titulo);
            const detalhes = notificacaoDetalhesExibicao(notificacao.detalhes);

            return (
              <div key={notificacao.id}>
                <div className="dropdown-item-text">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                      <div className="fw-bold small">{titulo}</div>
                      {detalhes != null && (
                        <div className="text-muted small" style={{ fontSize: '0.85rem' }}>
                          {detalhes}
                        </div>
                      )}
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {DataEHoraAmigavel(notificacao.criadoEm)}
                      </div>
                    </div>
                    {!notificacao.exibidapopup && (
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        title="Marcar como lida"
                        onClick={() => handleMarcarComoLida(notificacao.id)}
                      >
                        ✓
                      </button>
                    )}
                  </div>
                  {(() => {
                    const link = getLinkParaNotificacao(notificacao);
                    return link ? (
                      <Link
                        to={link}
                        className="btn btn-sm btn-link p-0 text-decoration-none"
                        onClick={() => setShowNotificacoes(false)}
                      >
                        Ver detalhes →
                      </Link>
                    ) : null;
                  })()}
                </div>
                {index < notificacoes.length - 1 && <div className="dropdown-divider" />}
              </div>
            );
          })
        )}

        {/* Seção 2 — Relatórios */}
        <div className="dropdown-divider my-0" />
        <div className="dropdown-header d-flex justify-content-between align-items-center py-2">
          <strong className="small text-uppercase text-muted" style={{ letterSpacing: '0.04em' }}>
            Relatórios
          </strong>
          {relatoriosCount > 0 && (
            <span className="badge rounded-pill bg-secondary" style={{ fontSize: '0.65rem' }}>
              {relatoriosCount}
            </span>
          )}
        </div>
        <div className="dropdown-divider my-0" />
        {loadingRelatorios && notificacoesRelatorios.length === 0 ? (
          <div className="dropdown-item-text text-muted text-center py-3 small">
            Carregando…
          </div>
        ) : notificacoesRelatorios.length === 0 ? (
          <div className="dropdown-item-text text-muted text-center py-3 small">
            Nenhuma notificação de relatórios
          </div>
        ) : (
          notificacoesRelatorios.map((item, index) => {
            const isClickable = item.tipo === 'validacao_pendente' && item.linkAprovacao;
            return (
              <div key={item.id}>
                <div
                  className="dropdown-item-text"
                  style={{ cursor: isClickable ? 'pointer' : undefined }}
                  onClick={
                    isClickable
                      ? () => {
                          setShowNotificacoes(false);
                          navigate(Links.Relatorios_Aprovacao);
                        }
                      : undefined
                  }
                  role={isClickable ? 'button' : undefined}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center gap-1 flex-wrap mb-1">
                        {iconeRelatorio(item.tipo)}
                        <span className="fw-bold small">{item.titulo}</span>
                        {item.relatorioTipo && (
                          <span
                            className="badge bg-light text-dark border"
                            style={{ fontSize: '0.65rem' }}
                          >
                            {getTipoLabelRelatorio(item.relatorioTipo)}
                          </span>
                        )}
                        {item.tipo === 'validacao_pendente' && (
                          <span className="badge bg-primary" style={{ fontSize: '0.65rem' }}>
                            Aprovação
                          </span>
                        )}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                        {item.mensagem}
                      </div>
                      {item.relatorioPeriodo && (
                        <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                          Período: {item.relatorioPeriodo}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-danger text-decoration-none flex-shrink-0"
                      style={{ fontSize: '1.25rem', lineHeight: 1, minWidth: '24px' }}
                      title="Ocultar notificação"
                      aria-label="Ocultar notificação"
                      onClick={(e) => {
                        e.stopPropagation();
                        ocultarNotificacao(item);
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
                {index < notificacoesRelatorios.length - 1 && <div className="dropdown-divider" />}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
